import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  TextField,
  Button,
  Box,
  CardContent,
  Card,
  MenuItem,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import GetAppIcon from "@mui/icons-material/GetApp"; // new export icon import (optional)
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { getLog } from "../../api/topicDataAPI";
import { useLocation } from "react-router-dom";
import { getNodeById } from "../../api/nodeAPI";
import { exportLogs } from "../../utils/logsExporter";
import { getPlaceIdFromPathname } from "../../utils/pathUtils";
import { useSnackbar } from "../../hooks/useSnackbar";
import { getCurrentTenant } from "../../config/tenantConfig";

// dtype , subject, severity, start, end, pagesize
// page start with 1
// pagesize default 20

export default function Logs() {
  const { t } = useTranslation();
  const location = useLocation();

  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? parseInt(rawId, 10) : null;
  const isRcu = getCurrentTenant().projectCode === "RCU";

  const columns = [
    {
      field: "subject",
      headerName: t("nodeComponents.logs.columns.subject"),
      flex: 0.8,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "severity",
      headerName: t("nodeComponents.logs.columns.severity"),
      flex: 0.5,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "message",
      headerName: t("nodeComponents.logs.columns.message"),
      flex: 2,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              lineHeight: "normal",
              whiteSpace: "normal",
              wordBreak: "break-word",
              padding: "8px",
              textAlign: isRcu ? "left" : "center",
              overflow: "visible",
            }}
          >
            {params.value}
          </div>
        );
      },
      cellClassName: "message-cell",
    },
    {
      field: "epoch",
      headerName: t("nodeComponents.logs.columns.time"),
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
  ];

  const [rows, setRows] = useState([]);
  const [allLogs, setAllLogs] = useState([]); // Tüm logları saklamak için yeni state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [severity, setSeverity] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [messageFilter, setMessageFilter] = useState(""); // Yeni message filter state
  const [loading, setLoading] = useState(false);
  const [nodeName, setNodeName] = useState("Node"); // Node ismi için state
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  // Fetch node name
  useEffect(() => {
    const fetchNodeName = async () => {
      if (!node_id) return;
      try {
        const data = await getNodeById(node_id);
        if (data) {
          setNodeName(data.name || "Node");
        }
      } catch (err) {
        console.error("Failed to fetch node name:", err);
      }
    };

    fetchNodeName();
  }, [node_id]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const logsData = await getLog(node_id);
        const safeLogsData = Array.isArray(logsData) ? logsData : [];
        setAllLogs(safeLogsData);
        setRows(
          safeLogsData.map((log) => ({
            ...log,
            id: log._id || `temp-${Date.now()}-${Math.random()}`,
          }))
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [node_id]);

  function handleFilterClick() {
    let filteredLogs = Array.isArray(allLogs) ? allLogs : [];

    if (filteredLogs.length > 0 && startDate) {
      const start = new Date(`${startDate}T00:00:00`);
      filteredLogs = filteredLogs.filter((log) => new Date(log.epoch) >= start);
    }
    if (filteredLogs.length > 0 && endDate) {
      const end = new Date(`${endDate}T23:59:59`);
      filteredLogs = filteredLogs.filter((log) => new Date(log.epoch) <= end);
    }
    if (filteredLogs.length > 0 && severity) {
      filteredLogs = filteredLogs.filter(
        (log) => String(log.severity) === String(severity)
      );
    }
    if (filteredLogs.length > 0 && subjectFilter) {
      const subjectKeywords = subjectFilter
        .split(",")
        .map((k) => k.trim().toLowerCase());
      filteredLogs = filteredLogs.filter((log) => {
        if (!log.subject) return false;
        const subjectArray = Array.isArray(log.subject)
          ? log.subject
          : [String(log.subject)];
        return subjectKeywords.some((keyword) =>
          subjectArray.some((sub) => sub.toLowerCase().includes(keyword))
        );
      });
    }
    if (filteredLogs.length > 0 && messageFilter) {
      const messageKeywords = messageFilter
        .split(",")
        .map((k) => k.trim().toLowerCase());
      filteredLogs = filteredLogs.filter((log) => {
        if (!log.message) return false;
        return messageKeywords.some((keyword) =>
          String(log.message).toLowerCase().includes(keyword)
        );
      });
    }
    setRows(
      filteredLogs.map((log) => ({
        ...log,
        id: log._id || `temp-${Date.now()}-${Math.random()}`,
      }))
    );
  }

  // Refresh handler
  async function handleRefresh() {
    setLoading(true);
    try {
      const logsData = await getLog(node_id);
      const safeLogsData = Array.isArray(logsData) ? logsData : [];
      setAllLogs(safeLogsData);
      setRows(
        safeLogsData.map((log) => ({
          ...log,
          id: log._id || `temp-${Date.now()}-${Math.random()}`,
        }))
      );
    } finally {
      setLoading(false);
    }
  }

  // Excel export handler
  function handleExport() {
    try {
      const result = exportLogs(rows, columns, nodeName);

      if (result.success) {
        showSnackbar(t('nodeComponents.logs.exportSuccess', { fileName: result.fileName }), 'success');
      } else {
        showSnackbar(result.error || t('nodeComponents.logs.exportError'), 'error');
      }
    } catch (error) {
      console.error('Excel export error:', error);
      showSnackbar(t('nodeComponents.logs.downloadError'), 'error');
    }
  }

  // RCU: left-align every header + cell.
  const displayColumns = isRcu
    ? columns.map((c) => ({ ...c, align: "left", headerAlign: "left" }))
    : columns;

  return (
    <>
      {" "}
      {/* <Box sx={{ width: "100%", maxWidth: "1700px", mx: "auto", px: 2 }}>
        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2 }}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Box sx={{ display: { xs: "none", sm: "block" }, flex: 1 }} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "space-between", sm: "flex-start" },
                }}
              >
                <Button
                  variant="contained"
                  color="third"
                  onClick={handleFilterClick}
                  startIcon={<FilterAltIcon />}
                  sx={{ height: 40, flex: { xs: 1, sm: "none" } }}
                >
                  {t("nodeComponents.logs.filter")}
                </Button>
                <Button
                  variant="contained"
                  color="third"
                  onClick={handleExport}
                  startIcon={<GetAppIcon />}
                  sx={{ height: 40, flex: { xs: 1, sm: "none" } }}
                >
                  {t("nodeComponents.logs.export")}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box> */}
      {/* Filter controls in a card with the same style */}{" "}
      <Box sx={{ width: "100%", maxWidth: "1700px", mx: "auto", px: 2 }}>
        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(5, 1fr)",
                },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("nodeComponents.logs.subjectFilter")}
                </Typography>
                <TextField
                  value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("nodeComponents.logs.logLevelFilter")}
                </Typography>
                <TextField
                  select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">{t("nodeComponents.logs.all")}</MenuItem>
                  <MenuItem value="1">1</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                  <MenuItem value="3">3</MenuItem>
                  <MenuItem value="4">4</MenuItem>
                  <MenuItem value="5">5</MenuItem>
                </TextField>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("nodeComponents.logs.messageFilter")}
                </Typography>
                <TextField
                  value={messageFilter}
                  onChange={(e) => setMessageFilter(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("nodeComponents.logs.dateFilter")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                  <TextField
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ width: "50%" }}
                  />
                  <TextField
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ width: "50%" }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "space-between", sm: "flex-start" },
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  color="third"
                  onClick={handleFilterClick}
                  startIcon={<FilterAltIcon />}
                  sx={{ height: 40, flex: { xs: 1, sm: "none" } }}
                  disabled={loading}
                >
                  {t("nodeComponents.logs.filter")}
                </Button>
                <Button
                  variant="contained"
                  color="third"
                  onClick={handleExport}
                  startIcon={<GetAppIcon />}
                  sx={{ height: 40, flex: { xs: 1, sm: "none" } }}
                  disabled={loading}
                >
                  {t("nodeComponents.logs.export")}
                </Button>
                <Button
                  variant="contained"
                  color="third"
                  onClick={handleRefresh}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  sx={{ height: 40, flex: { xs: 1, sm: "none" } }}
                  disabled={loading}
                >
                  {t("nodeComponents.logs.refresh")}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>{" "}
      </Box>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          "& .MuiDataGrid-root": {
            minWidth: "800px", // Ensures the DataGrid doesn't shrink too much
          },
        }}
      >
        {" "}
        <DataGrid
          columns={displayColumns}
          rows={rows}
          getRowId={(row) => row.id}
          pagination
          loading={loading}
          initialState={{
            sorting: { sortModel: [{ field: "epoch", sort: "desc" }] },
            pagination: {
              paginationModel: { pageSize: 20 },
            },
          }}
          pageSizeOptions={[20]}
          disableSelectionOnClick
          getRowHeight={() => "auto"}
          getEstimatedRowHeight={() => 100}
          sx={{
            width: "100%" /* DataGrid genişliğini tam ayarla */,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
            "& .MuiDataGrid-cell": {
              alignItems: "center",
              display: "flex",
              whiteSpace: "normal",
              lineHeight: "normal",
              padding: "8px",
            },
            "& .MuiDataGrid-cell.message-cell": {
              minHeight: "unset !important",
              maxHeight: "unset !important",
              height: "auto !important",
              alignItems: "flex-start",
              whiteSpace: "normal",
              overflow: "visible",
            },
            "& .MuiDataGrid-row": {
              maxHeight: "unset !important",
              minHeight: "48px !important",
            },
            "& .MuiDataGrid-main": {
              width: "100%",
              overflow: "auto",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto",
            },
            /* DataGrid horizontally scrollable */
            "& .MuiDataGrid-root": {
              minWidth: "800px",
            },
            "& .MuiDataGrid-columnHeader": {
              minWidth: "100px" /* Başlıklar için minimum genişlik */,
            },
          }}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
